import { MODE, ZOOM_LEVEL, ZOOM_SETTINGS } from '../../constants';
import axios from 'axios';
import { DateTimeFormatter, DayOfWeek, LocalDate, Period, TemporalAdjusters } from '@js-joda/core';
import { calculateDragChanges } from './calculateDragChanges';
import { maxBy } from 'lodash';
import { emitter } from '../../plugins/Mitt';

const dateformatter = DateTimeFormatter.ofPattern('yyyy-MM-dd');

const CancelToken = axios.CancelToken;
let cancelRequestItemSlots;
let cancelRequestDefaultWeekschedules;
let cancelRequestItemWeekschedules;
let cancelRequestCompanyAbsences;
let cancelRequestItemAbsences;
let cancelSwitchMode;
let cancelRequestCompanyCapacity;
let cancelRequestItemCapacity;

export default {
    namespaced: true,
    strict: true, //debug,
    state: {
        // planning
        mode: '',
        statusColors: 'tasks',
        link_assets: true,
        link_dates: true,
        showDescriptionInCards: false,
        slotTitles: {},
        calendarHeight: 0,
        slotTitlesAmount: 0,
        slotTitlesHeigth: 0,
        // Sort
        sort: '',
        sortOrder: '',
        // Search
        search: '',
        // Filters
        statusesToShow: [],
        // Zoom
        currentZoom: ZOOM_LEVEL.week,
        currentAmountToShow: ZOOM_SETTINGS[ZOOM_LEVEL.week].amountToShow,
        // Dates
        dateToShow: null,
        dateStart: null,
        dateEnd: null,
        dates: [],
        // Rows
        titleRowWidth: 0,
        runwayRows: 0,
        defaultRowHeight: 0,
        rows: [],
        rowsTotalCount: null,
        requestRowsInProgress: false,
        // Weekschedules
        defaultWeekschedule: {},
        companyWeekschedule: {},
        itemsWeekschedule: {},
        // Absences
        companyAbsences: {},
        itemsAbsences: {},
        // Slots
        slots: {},
        slotGroups: {},
        //Items
        items: {},
        itemsOrder: [],
        // Mappings to show slots in right position/styling
        itemsSlotsMapping: {},
        itemsSlotsGroupMapping: {},
        // Persons
        persons: {},
        // Equipments
        equipments: {},
        // Tasks
        tasks: {},
        // Teams
        teams: {},
        // Capacity
        capacityItems: {},
        capacityCompany: {},
        // Dragging
        isDragging: false,
        dragStartInfo: {},
    },
    getters: {},
    mutations: {
        setCalendarHeight(state, calendarheight) {
            state.calendarHeight = calendarheight;
        },
        setStatusesToShow(state, statuses) {
            state.statusesToShow = statuses;
        },
        setCurrentZoom(state, zoomLevel) {
            state.currentZoom = zoomLevel;
            state.currentAmountToShow = ZOOM_SETTINGS[zoomLevel].amountToShow;
        },
        setCurrentAmountToShow(state, amountToShow) {
            state.currentAmountToShow = amountToShow;
        },
        setMode(state, mode) {
            const defaultSort = _.find(MODE[mode].sort, 'default');

            state.mode = mode;
            state.sort = defaultSort?.sort;
            state.sortOrder = defaultSort?.sortOrder;

            if (mode === 'dailyPlanned' || mode === 'teamLead') {
                state.currentZoom = ZOOM_LEVEL.day;
                state.currentAmountToShow = 1;
            }
            if (mode === 'projectPersons') {
                state.currentZoom = ZOOM_LEVEL.week;
                state.currentAmountToShow = 2;
            }
            state.slotTitlesAmount = Object.keys(state.slotTitles).filter(function (key) {
                return state.slotTitles[key];
            }).length;
            const lineHeight = 15;
            const timeAndAssigneesHeight = 21;
            state.slotTitlesHeigth = state.slotTitlesAmount * lineHeight + timeAndAssigneesHeight;
        },
        setStatusColors(state, statusColors) {
            state.statusColors = statusColors;
        },
        setShowDescriptionInCards(state, showDescription) {
            state.showDescriptionInCards = showDescription;
        },
        setSlotTitles(state, slotTitles) {
            state.slotTitles = slotTitles;
            const slotTitlesAmount = Object.keys(slotTitles).filter(function (key) {
                return slotTitles[key];
            }).length;
            state.slotTitlesAmount = slotTitlesAmount;
            const lineHeight = 15;
            const timeAndAssigneesHeight = 21;
            state.slotTitlesHeigth = slotTitlesAmount * lineHeight + timeAndAssigneesHeight;
        },
        setSort(state, { sort, sortOrder }) {
            state.sort = sort;
            state.sortOrder = sortOrder;
        },
        setSearch(state, { search }) {
            state.search = search;
        },
        resetData(state) {
            state.itemsSlotsGroupMapping = {};
            state.itemsSlotsMapping = {};
            state.rows = [];
            state.items = {};
            state.itemsOrder = [];
            state.defaultWeekschedule = {};
            state.companyWeekschedule = {};
            state.itemsWeekschedule = {};
            state.companyAbsences = {};
            state.itemsAbsences = {};
            state.rowsTotalCount = null;
            state.capacityItems = {};
            state.capacityCompany = {};
        },
        setRequestRowsInProgress(state, value) {
            state.requestRowsInProgress = value;
        },
        setRunwayRowsToRender(state) {
            let rowHeight = ZOOM_SETTINGS[state.currentZoom].rowHeight;
            if (state.mode === 'capacityPerson') {
                rowHeight = ZOOM_SETTINGS[state.currentZoom].capacityRowHeight;
            }
            const tombstonesInView = Math.ceil(state.calendarHeight / rowHeight);
            state.runwayRows = tombstonesInView;
            state.defaultRowHeight = rowHeight;
        },
        addTombstoneRows(state, { totalRows }) {
            if (state.rowsTotalCount !== null && totalRows > state.rowsTotalCount) {
                totalRows = state.rowsTotalCount;
            }
            let rowAmount = state.rows.length;
            if (rowAmount >= totalRows) {
                return;
            }

            let rows = [];
            while (rowAmount < totalRows) {
                rows.push({
                    index: rowAmount,
                    height: 0,
                    itemRowHeight: 0,
                    showGroupRow: false,
                });
                rowAmount++;
            }
            state.rows = [...state.rows, ...rows];
        },
        setDefaultWeekschedule(state, { defaultWeekschedule }) {
            state.defaultWeekschedule = Object.freeze({
                ...state.defaultWeekschedule,
                ...defaultWeekschedule,
            });
        },
        setCompanyWeekschedule(state, { companyWeekschedule }) {
            state.companyWeekschedule = Object.freeze({
                ...state.companyWeekschedule,
                ...companyWeekschedule,
            });
        },
        setItemsLoaded(state, { ids }) {
            ids.forEach((id) => {
                let item = Object.assign({}, state.items[id], {
                    isLoaded: true,
                });
                Object.freeze(item);
                Vue.set(state.items, id, item);
            });
        },
        setItemRequestData(state, { data }) {
            const total = data.total;
            state.rowsTotalCount = total;
            if (state.rows.length > total) {
                state.rows.length = total;
            }

            state.items = {
                ...state.items,
                ...data.items,
            };

            state.itemsOrder = [...state.itemsOrder, ...data.itemsOrder];
        },
        insertItemOnIndex(state, { item, index }) {
            state.items[item.id] = item;
            state.itemsOrder.splice(index, 0, item.id);

            let rows = _.cloneDeep(state.rows);

            rows.splice(index, 0, {
                index: index,
                height: 0,
                itemRowHeight: 0,
                showGroupRow: false,
            });

            rows.forEach((row, index) => {
                row.index = index;
            });

            state.rows = rows;
        },
        cacheRowInfo(state) {
            let rows = _.cloneDeep(state.rows);
            let index = 0;
            for (index; index < rows.length; index++) {
                let row = rows[index];
                if (row.height > 0) {
                    continue;
                }

                const itemId = state.itemsOrder[index];
                const item = state.items[itemId];
                if (!item) {
                    continue;
                }

                const previousItemId = state.itemsOrder[index - 1];
                const previousItem = state.items[previousItemId];

                let showGroupRow = !!item.group_title;
                if( state.mode === 'dailyPlanned' || state.mode === 'teamLead' ){
                    showGroupRow = false
                }
                else if (item && previousItem) {
                    showGroupRow = previousItem.group_id !== item.group_id;
                }
                row.showGroupRow = showGroupRow;

                let height = state.defaultRowHeight;
                const itemRowHeight = height;
                if (showGroupRow) {
                    height += 25;
                }
                row.height = height;
                row.itemRowHeight = itemRowHeight;
            }
            state.rows = rows;
        },
        setInfoRequestData(state, { data }) {
            Object.keys(data.slots).forEach((slotId) => {
                let slot = data.slots[slotId];
                slot['localDate'] = LocalDate.parse(slot.date);
                data.slots[slotId] = slot;
            });
            state.slots = Object.freeze({
                ...state.slots,
                ...data.slots,
            });

            let slotGroups = {};
            Object.keys(data.slots).forEach((id) => {
                const slot = data.slots[id];
                if (!slotGroups[slot.first_slot_id]) {
                    slotGroups[slot.first_slot_id] = [];
                }
                slotGroups[slot.first_slot_id].push(slot.id);
            });

            state.slotGroups = Object.freeze({
                ...state.slotGroups,
                ...slotGroups,
            });

            state.tasks = Object.freeze({
                ...state.tasks,
                ...data.tasks,
            });

            state.persons = Object.freeze({
                ...state.persons,
                ...data.persons,
            });

            state.equipments = Object.freeze({
                ...state.equipments,
                ...data.equipments,
            });

            state.teams = Object.freeze({
                ...state.teams,
                ...data.teams,
            });
        },
        setWeekschedules(state, { itemsWeekschedule }) {
            state.itemsWeekschedule = Object.freeze({
                ...state.itemsWeekschedule,
                ...itemsWeekschedule,
            });
        },
        setCompanyAbsences(state, { companyAbsences }) {
            state.companyAbsences = Object.freeze({
                ...state.companyAbsences,
                ...companyAbsences,
            });
        },
        setAbsences(state, { itemsAbsences }) {
            state.itemsAbsences = Object.freeze({
                ...state.itemsAbsences,
                ...itemsAbsences,
            });
        },
        setItemsCapacity(state, { data }) {
            state.capacityItems = Object.freeze({
                ...state.capacityItems,
                ...data,
            });
        },
        setCompanyCapacity(state, { data }) {
            state.capacityCompany = Object.freeze({
                ...state.capacityCompany,
                ...data,
            });
        },
        // Dates
        setDates(state) {
            const queryString = window.location.search;
            const urlParams = new URLSearchParams(queryString);
            let titleRowWidth = 250;

            let date;
            try {
                date = LocalDate.parse(urlParams.get('date'));
            } catch (err) {
                date = LocalDate.now();
            }

            state.dateToShow = date;

            let start = date;

            const currentZoom = state.currentZoom;
            let amountToShow = state.currentAmountToShow;

            let end = date;
            if (currentZoom === ZOOM_LEVEL.day) {
                amountToShow--;
                end = end.plusDays(amountToShow);
            } else if (currentZoom === ZOOM_LEVEL.week) {
                amountToShow--;

                end = end
                    .plusWeeks(amountToShow)
                    .with(TemporalAdjusters.nextOrSame(DayOfWeek.SUNDAY));
                start = start.with(TemporalAdjusters.previousOrSame(DayOfWeek.MONDAY));
            } else {
                end = end
                    .plusMonths(amountToShow)
                    .with(TemporalAdjusters.nextOrSame(DayOfWeek.SUNDAY));
                start = start.with(TemporalAdjusters.previousOrSame(DayOfWeek.MONDAY));
            }

            state.dateStart = start;
            state.dateEnd = end;

            let days = [];
            days.push({
                localDate: start,
                formatted: start.format(dateformatter),
            });
            titleRowWidth += ZOOM_SETTINGS[currentZoom].dayWidth[start.dayOfWeek().value()];

            while (start.isBefore(end)) {
                start = start.plusDays(1);
                days.push({
                    localDate: start,
                    formatted: start.format(dateformatter),
                });

                // Calculate The title row width
                titleRowWidth += ZOOM_SETTINGS[currentZoom].dayWidth[start.dayOfWeek().value()];
            }

            state.titleRowWidth = titleRowWidth;
            state.dates = days;
        },
        editSlots(state, { slots }) {
            state.slots = Object.freeze({
                ...state.slots,
                ...slots,
            });

            Object.keys(slots).forEach((id) => {
                let slot = slots[id];
                if (!state.slotGroups[slot.first_slot_id]) {
                    state.slotGroups[slot.first_slot_id] = [];
                }
                state.slotGroups[slot.first_slot_id].push(slot.id);
            });
        },

        setRowsCalculation(state, { rowsCalculation, itemsSlotsGroupMapping, itemsSlotsMapping }) {
            let rows = _.cloneDeep(state.rows);
            let index = 0;
            for (index; index < rows.length; index++) {
                let row = rows[index];
                const rowCalculation = rowsCalculation[index];
                if (!rowCalculation) {
                    continue;
                }
                row.height = rowCalculation.height;
                row.itemRowHeight = rowCalculation.itemRowHeight;
            }
            state.rows = rows;

            state.itemsSlotsGroupMapping = Object.freeze({
                ...state.itemsSlotsGroupMapping,
                ...itemsSlotsGroupMapping,
            });

            state.itemsSlotsMapping = Object.freeze({
                ...state.itemsSlotsMapping,
                ...itemsSlotsMapping,
            });
        },

        // Dragging
        resetDragStartInfo(state) {
            state.dragStartInfo = {};
        },
        setDragStartData(
            state,
            { slotId, date, localDate, firstSlotId, taskId, personId, equipmentId, teamId, dragTime },
        ) {
            state.isDragging = true;

            const slotGroupsIds = state.slotGroups[firstSlotId];
            let slots = {};
            slotGroupsIds.forEach((slotId) => {
                const slot = state.slots[slotId];
                slots[slotId] = slot;
            });

            const persons = state.slots[slotId].persons;
            const equipments = state.slots[slotId].equipments;
            const teams = state.slots[slotId].teams;
            state.dragStartInfo = {
                draggedSlot: {
                    slotId: slotId,
                    date: date,
                    dragTime: dragTime,
                    localDate: localDate,
                    firstSlotId: firstSlotId,
                    taskId: taskId,
                    personId: personId,
                    equipmentId: equipmentId,
                    teamId: teamId,
                    persons: persons,
                    equipments: equipments,
                    teams: teams,
                },
                affectedSlots: slots,
            };
        },
        setDragChanges(state, { slots, persons, equipments, tasks, teams }) {
            state.slots = Object.freeze({
                ...state.slots,
                ...slots,
            });
            state.persons = Object.freeze({
                ...state.persons,
                ...persons,
            });
            state.equipments = Object.freeze({
                ...state.equipments,
                ...equipments,
            });
            state.tasks = Object.freeze({
                ...state.tasks,
                ...tasks,
            });
            state.teams = Object.freeze({
                ...state.teams,
                ...teams,
            });
        },
        setDragEnd(state) {
            state.isDragging = false;
        },
        setLinkAssets(state, value) {
            state.link_assets = value;
        },
        setLinkDates(state, value) {
            state.link_dates = value;
        },
    },
    actions: {
        async initCalendar({ state, commit, dispatch }, { calendarHeight }) {
            commit('setCalendarHeight', calendarHeight);
            await dispatch('getSettings');
            dispatch('permissions/getUserPermissions', null, { root: true });
            dispatch('resetCalendar');
        },
        resetCalendar({ state, commit, dispatch }) {
            commit('resetData');
            commit('setDates');

            dispatch('requestDefaultAndCompanyWeekschedule');
            // For Daily planned, this works in another way
            if( state.mode === 'dailyPlanned' || state.mode === 'projectPersons' || state.mode === 'teamLead' ){
                return
            }
            commit('setRunwayRowsToRender');
            commit('addTombstoneRows', { totalRows: state.runwayRows });
            dispatch('requestCompanyAbsences');
            dispatch('maybeRequestRows');
            dispatch('requestCompanyCapacity');
        },
        zoomIn({ state, commit, dispatch }, date) {
            const currentZoom = state.currentZoom;
            if (currentZoom < 1) {
                return;
            }

            if (MODE[state.mode].type === 'capacity' && currentZoom < 2) {
                return;
            }

            if (date) {
                var queryParams = new URLSearchParams(window.location.search);
                queryParams.set('date', date.localDate.format(dateformatter));
                history.pushState(null, null, '?' + queryParams.toString());
                commit('setDates');
            }
            commit('setCurrentZoom', currentZoom - 1);
            dispatch('resetCalendar');
        },
        zoomOut({ state, commit, dispatch }, date) {
            const currentZoom = state.currentZoom;
            if (currentZoom > 1) {
                return;
            }

            if (date) {
                var queryParams = new URLSearchParams(window.location.search);
                queryParams.set('date', date.localDate.format(dateformatter));
                history.pushState(null, null, '?' + queryParams.toString());
                commit('setDates');
            }

            commit('setCurrentZoom', currentZoom + 1);
            dispatch('resetCalendar');
        },
        setPreviousDates({ state, commit, dispatch }) {
            if (cancelRequestDefaultWeekschedules) {
                cancelRequestDefaultWeekschedules();
            }
            if (cancelRequestItemSlots) {
                cancelRequestItemSlots();
            }
            if (cancelRequestItemWeekschedules) {
                cancelRequestItemWeekschedules();
            }
            if (cancelRequestCompanyAbsences) {
                cancelRequestCompanyAbsences();
            }
            if (cancelRequestItemAbsences) {
                cancelRequestItemAbsences();
            }

            // Capacity
            if (cancelRequestCompanyCapacity) {
                cancelRequestCompanyCapacity();
            }
            if (cancelRequestItemCapacity) {
                cancelRequestItemCapacity();
            }

            const currentZoom = state.currentZoom;
            let date = state.dateStart;

            if (currentZoom === ZOOM_LEVEL.day) {
                date = date.minusDays(1);
            } else if (currentZoom === ZOOM_LEVEL.week) {
                date = date.minusWeeks(1);
            } else {
                date = date.minusMonths(1);
            }

            var queryParams = new URLSearchParams(window.location.search);
            queryParams.set('date', date.format(dateformatter));
            history.pushState(null, null, '?' + queryParams.toString());

            commit('setDates');

            if (MODE[state.mode].type === 'capacity') {
                dispatch('requestCompanyCapacity');
                dispatch('requestItemCapacity', { itemsIds: Object.keys(state.items) });
            } else {
                dispatch('requestItemsPlanning', { itemsIds: Object.keys(state.items) });
            }
        },
        setNextDates({ state, commit, dispatch }) {
            if (cancelRequestDefaultWeekschedules) {
                cancelRequestDefaultWeekschedules();
            }
            if (cancelRequestItemSlots) {
                cancelRequestItemSlots();
            }
            if (cancelRequestItemWeekschedules) {
                cancelRequestItemWeekschedules();
            }
            if (cancelRequestCompanyAbsences) {
                cancelRequestCompanyAbsences();
            }
            if (cancelRequestItemAbsences) {
                cancelRequestItemAbsences();
            }

            // Capacity
            if (cancelRequestCompanyCapacity) {
                cancelRequestCompanyCapacity();
            }
            if (cancelRequestItemCapacity) {
                cancelRequestItemCapacity();
            }

            const currentZoom = state.currentZoom;
            let date = state.dateStart;

            if (currentZoom === ZOOM_LEVEL.day) {
                date = date.plusDays(1);
            } else if (currentZoom === ZOOM_LEVEL.week) {
                date = date.plusWeeks(1);
            } else {
                date = date.plusMonths(1);
            }

            var queryParams = new URLSearchParams(window.location.search);
            queryParams.set('date', date.format(dateformatter));
            history.pushState(null, null, '?' + queryParams.toString());

            commit('setDates');
            if (MODE[state.mode].type === 'capacity') {
                dispatch('requestCompanyCapacity');
                dispatch('requestItemCapacity', { itemsIds: Object.keys(state.items) });
            } else {
                dispatch('requestItemsPlanning', { itemsIds: Object.keys(state.items) });
            }
        },
        // aparte serviceprovider, en dan routes naar die routes file zodat enkel toegelange endpoints beschikbaar zijn
        async getSettings({ commit, dispatch }) {
            await axios.get(route('planning.settings')).then((response) => {
                const queryString = window.location.search;
                const urlParams = new URLSearchParams(queryString);
                let type = urlParams.get('type');
                if (type && type !== '') {
                    commit('setMode', type);
                } else {
                    commit('setMode', response.data.mode);
                }

                let sort = urlParams.get('sort');
                if( sort ){
                    commit('setSort', { sort, sortOrder: 'ASC' });
                }

                commit('setShowDescriptionInCards', response.data.show_project_description);
                commit('setSlotTitles', response.data.slot_titles);
                commit('setStatusColors', response.data.status_colors);
                commit('setLinkAssets', response.data.link_assets);
                commit('setLinkDates', response.data.link_dates);
            });
        },
        changeMode({ state, commit, dispatch }, { mode }) {
            if (cancelRequestDefaultWeekschedules) {
                cancelRequestDefaultWeekschedules();
            }
            if (cancelSwitchMode) {
                cancelSwitchMode();
            }
            if (cancelRequestItemSlots) {
                cancelRequestItemSlots();
            }
            if (cancelRequestItemWeekschedules) {
                cancelRequestItemWeekschedules();
            }
            if (cancelRequestCompanyAbsences) {
                cancelRequestCompanyAbsences();
            }
            if (cancelRequestItemAbsences) {
                cancelRequestItemAbsences();
            }

            emitter.emit('hide-control-panel-extra-component');

            commit('setSearch', { search: '' });
            commit('setMode', mode);
            if (mode === 'capacityPerson' && state.currentZoom < ZOOM_LEVEL.week) {
                commit('setCurrentZoom', ZOOM_LEVEL.week)
            }

            dispatch('resetCalendar');
        },
        changeSort({ commit, dispatch }, { sort, sortOrder }) {
            commit('setSort', { sort, sortOrder });
            dispatch('resetCalendar');
        },
        changeSearch({ commit, dispatch }, { search }) {
            commit('setSearch', { search });
            dispatch('resetCalendar');
        },
        loadMoreRows({ state, commit, dispatch }) {
            let totalRows = state.rows.length + state.runwayRows;

            commit('addTombstoneRows', { totalRows: totalRows });
            dispatch('maybeRequestRows');
        },
        requestDefaultAndCompanyWeekschedule({ state, commit }) {
            axios
                .get(route('planning.get-default-and-company-weekschedule'), {
                    params: {
                        mode: state.mode,
                        dateStart: state.dateStart.format(dateformatter),
                        dateEnd: state.dateEnd.format(dateformatter),
                    },
                    cancelToken: new CancelToken(function executor(c) {
                        // An executor function receives a cancel function as a parameter
                        cancelRequestDefaultWeekschedules = c;
                    }),
                })
                .then((response) => {
                    commit('setDefaultWeekschedule', {
                        defaultWeekschedule: response.data.defaultWeekschedule,
                    });
                    commit('setCompanyWeekschedule', {
                        companyWeekschedule: response.data.companyWeekschedule,
                    });
                });
        },
        requestCompanyAbsences({ state, commit }) {
            if (state.mode !== 'project') {
                return;
            }
            axios
                .get(route('planning.get-company-absences'), {
                    params: {
                        dateStart: state.dateStart.format(dateformatter),
                        dateEnd: state.dateEnd.format(dateformatter),
                    },
                    cancelToken: new CancelToken(function executor(c) {
                        // An executor function receives a cancel function as a parameter
                        cancelRequestCompanyAbsences = c;
                    }),
                })
                .then((response) => {
                    commit('setCompanyAbsences', {
                        companyAbsences: response.data,
                    });
                });
        },
        maybeRequestRows({ state, commit, dispatch }) {
            if (state.requestRowsInProgress) return;

            const rowsAmount = state.rows.length;
            const loadedItems = state.itemsOrder.length;
            let itemsNeeded = rowsAmount - loadedItems;

            if (itemsNeeded <= 0) return;
            if (itemsNeeded < 10) {
                itemsNeeded = 10;
            }

            commit('setRequestRowsInProgress', true);
            commit('addTombstoneRows', { totalRows: loadedItems + itemsNeeded });

            const sort = state.sort;
            const sortOrder = state.sortOrder;
            const search = state.search;
            const statusesToShow = state.statusesToShow;
            const routeName = MODE[state.mode].route;

            axios
                .get(route(routeName), {
                    params: {
                        search: search,
                        sort: sort,
                        sortOrder: sortOrder,
                        offset: loadedItems,
                        limit: itemsNeeded,
                        statusesToShow: statusesToShow,
                        dateStart: state.dateStart.format(dateformatter),
                        dateEnd: state.dateEnd.format(dateformatter),
                    },
                    cancelToken: new CancelToken(function executor(c) {
                        // An executor function receives a cancel function as a parameter
                        cancelSwitchMode = c;
                    }),
                })
                .then((response) => {
                    commit('setItemRequestData', { data: response.data });
                    commit('cacheRowInfo');
                    commit('setRequestRowsInProgress', false);

                    dispatch('maybeRequestRows');

                    const itemsToLoad = Object.keys(response.data.items).map(Number);

                    if (MODE[state.mode].type === 'capacity') {
                        dispatch('requestItemCapacity', { itemsIds: itemsToLoad });
                    } else {
                        dispatch('requestItemsPlanning', { itemsIds: itemsToLoad });
                    }
                });
        },
        requestItemsPlanning({ dispatch }, { itemsIds = [] }) {
            dispatch('requestDefaultAndCompanyWeekschedule');
            dispatch('requestCompanyAbsences');
            dispatch('requestItemSlots', { itemsIds });
            dispatch('requestItemWeekschedule', { itemsIds });
            dispatch('requestItemAbsences', { itemsIds });
        },
        requestItemSlots(
            { state, commit, dispatch },
            { itemsIds = [], fromRefreshContextMenu = false },
        ) {
            if (itemsIds.length < 1) return;

            axios
                .get(route('planning.slots'), {
                    params: {
                        dateStart: state.dateStart.format(dateformatter),
                        dateEnd: state.dateEnd.format(dateformatter),
                        itemIds: itemsIds,
                        mode: state.mode,
                        fromRefreshContextMenu: fromRefreshContextMenu,
                    },
                    cancelToken: new CancelToken(function executor(c) {
                        // An executor function receives a cancel function as a parameter
                        cancelRequestItemSlots = c;
                    }),
                })
                .then((response) => {
                    commit('setInfoRequestData', { data: response.data });

                    dispatch('calculateSlotPositions', { itemsIds: itemsIds });
                });
        },
        requestItemWeekschedule({ state, commit, dispatch }, { itemsIds = [] }) {
            if (itemsIds.length < 1) return;

            axios
                .get(route('planning.weekschedules'), {
                    params: {
                        dateStart: state.dateStart.format(dateformatter),
                        dateEnd: state.dateEnd.format(dateformatter),
                        itemIds: itemsIds,
                        mode: state.mode,
                    },
                    cancelToken: new CancelToken(function executor(c) {
                        // An executor function receives a cancel function as a parameter
                        cancelRequestItemWeekschedules = c;
                    }),
                })
                .then((response) => {
                    commit('setWeekschedules', { itemsWeekschedule: response.data });
                });
        },
        requestItemAbsences({ state, commit, dispatch }, { itemsIds = [] }) {
            if (itemsIds.length < 1) return;

            axios
                .get(route('planning.absences'), {
                    params: {
                        dateStart: state.dateStart.format(dateformatter),
                        dateEnd: state.dateEnd.format(dateformatter),
                        itemIds: itemsIds,
                        mode: state.mode,
                    },
                    cancelToken: new CancelToken(function executor(c) {
                        // An executor function receives a cancel function as a parameter
                        cancelRequestItemAbsences = c;
                    }),
                })
                .then((response) => {
                    commit('setAbsences', { itemsAbsences: response.data });
                    // dispatch('calculateAbsencePositions', { itemsIds: itemsIds });
                });
        },
        requestTaskIndex({ state, commit }, { taskId }) {
            const url = route('planning.get-task-index', taskId);

            const sort = state.sort;
            const sortOrder = state.sortOrder;
            const search = state.search;
            const statusesToShow = state.statusesToShow;

            axios
                .get(url, {
                    params: {
                        search: search,
                        sort: sort,
                        sortOrder: sortOrder,
                        statusesToShow: statusesToShow,
                    },
                })
                .then((response) => {
                    const item = {
                        id: response.data.id,
                        title: response.data.title,
                        group_id: response.data.group_id,
                        group_title: response.data.group_title,
                        group_subtitle: response.data.group_subtitle,
                    };
                    const index = response.data.row_index;

                    if (index < state.rowsTotalCount) {
                        commit('insertItemOnIndex', {
                            item: item,
                            index: index,
                        });
                        commit('cacheRowInfo');
                    }
                });
        },

        requestItemCapacity({ state, commit, dispatch }, { itemsIds = [] }) {
            if (itemsIds.length < 1) return;

            axios
                .get(route('capacity.persons'), {
                    params: {
                        dateStart: state.dateStart.format(dateformatter),
                        dateEnd: state.dateEnd.format(dateformatter),
                        itemIds: itemsIds,
                        mode: state.mode,
                    },
                    cancelToken: new CancelToken(function executor(c) {
                        // An executor function receives a cancel function as a parameter
                        cancelRequestItemCapacity = c;
                    }),
                })
                .then((response) => {
                    commit('setItemsCapacity', { data: response.data });
                });
        },
        requestCompanyCapacity({ state, commit }) {
            if (MODE[state.mode].type !== 'capacity') {
                return;
            }

            axios
                .get(route('capacity.company'), {
                    params: {
                        dateStart: state.dateStart.format(dateformatter),
                        dateEnd: state.dateEnd.format(dateformatter),
                    },
                    cancelToken: new CancelToken(function executor(c) {
                        // An executor function receives a cancel function as a parameter
                        cancelRequestCompanyCapacity = c;
                    }),
                })
                .then((response) => {
                    commit('setCompanyCapacity', { data: response.data });
                });
        },

        // calculateSlotPositions
        calculateSlotPositions: function ({ state, commit }, { itemsIds }) {
            itemsIds = itemsIds.map(Number);

            const rows = state.rows;
            const itemsOrder = state.itemsOrder;
            const slots = state.slots;
            const slotGroups = state.slotGroups;

            const calendarDates = state.dates;
            const firstCalendarDate = calendarDates[0].localDate;
            const lastCalendarDate = calendarDates[calendarDates.length - 1].localDate;

            const currentZoom = state.currentZoom;
            const defaultRowHeight = state.defaultRowHeight;
            const hourHeight = ZOOM_SETTINGS[currentZoom].hourHeight;
            const dayWidths = ZOOM_SETTINGS[currentZoom].dayWidth;

            const mode = state.mode;
            let items = {};
            switch (mode) {
                case 'project':
                    items = state.tasks;
                    break;
                case 'team':
                    items = state.teams;
                    break;
                case 'equipment':
                    items = state.equipments;
                    break;
                case 'person':
                default:
                    items = state.persons;
                    break;
            }

            let rowsCalculation = [];
            let itemsSlotsMapping = {};
            let itemsSlotsGroupMapping = {};

            rows.forEach((row) => {
                let maxHourRow = null;
                const itemId = itemsOrder[row.index];

                if (!itemId || itemsIds.indexOf(itemId) < 0) {
                    return;
                }

                const item = items[itemId];
                if (!item) {
                    return;
                }
                const itemSlots = item.slots;

                let itemSlotsForDate = {};

                itemsSlotsGroupMapping[itemId] = {};
                itemsSlotsMapping[itemId] = {};

                calendarDates.forEach((date) => {
                    itemsSlotsMapping[itemId][date.formatted] = {};
                });

                itemSlots.forEach((slotId) => {
                    // Get the slot data
                    const slot = slots[slotId];
                    if (!slot) {
                        return;
                    }

                    const slotDate = slot.localDate;

                    if (
                        slotDate.isBefore(firstCalendarDate) ||
                        slotDate.isAfter(lastCalendarDate)
                    ) {
                        return;
                    }

                    // Index by date
                    if (!itemSlotsForDate[slot.date]) {
                        itemSlotsForDate[slot.date] = [];
                    }
                    itemSlotsForDate[slot.date].push(slot);

                    // Check for slot previous day
                    const dayBefore = slotDate.minusDays(1).format(dateformatter);
                    const hasSlotDayBefore = slotGroups[slot.first_slot_id].some((slotId) => {
                        return slots[slotId].date == dayBefore;
                    });

                    const showSlotTitle = !hasSlotDayBefore || slotDate.isEqual(firstCalendarDate);

                    let titleWidth = 0;
                    if (showSlotTitle) {
                        let slotWidth = dayWidths[slotDate.dayOfWeek().value()];
                        let dateToCheck = slotDate.plusDays(1);

                        while (
                            slotGroups[slot.first_slot_id].some((slotId) => {
                                return slots[slotId].date == dateToCheck.format(dateformatter);
                            })
                        ) {
                            if (
                                dateToCheck.isBefore(firstCalendarDate) ||
                                dateToCheck.isAfter(lastCalendarDate)
                            ) {
                                break;
                            }
                            slotWidth += dayWidths[dateToCheck.dayOfWeek().value()];
                            dateToCheck = dateToCheck.plusDays(1);
                        }

                        titleWidth = slotWidth - 25;
                    }

                    // Check for overlap in day
                    const slotsWithTimeOverlap = Object.values(
                        itemsSlotsMapping[itemId][slot.date],
                    ).filter((slotToCheck) => {
                        const slotStart = slot.date + slot.time_start;
                        const slotEnd = slot.date + slot.time_end;
                        const slotToCheckStart = slotToCheck.date + slotToCheck.timeStart;
                        const slotToCheckEnd = slotToCheck.date + slotToCheck.timeEnd;

                        return (
                            (slotToCheckStart >= slotStart && slotToCheckStart < slotEnd) ||
                            (slotToCheckEnd > slotStart && slotToCheckEnd < slotEnd) ||
                            (slotToCheckStart <= slotStart && slotToCheckEnd > slotEnd)
                        );
                    });

                    const hourRow =
                        slotsWithTimeOverlap.length === 0
                            ? 0
                            : maxBy(slotsWithTimeOverlap, 'hourRow').hourRow + 1;

                    maxHourRow = maxHourRow === null || maxHourRow < hourRow ? hourRow : maxHourRow;

                    itemsSlotsMapping[itemId][slot.date][slot.id] = {
                        id: slot.id,
                        repeatedSettingsId: slot.repeated_settings_id,
                        hourRow: hourRow,
                        date: slot.date,
                        timeStart: slot.time_start,
                        timeEnd: slot.time_end,
                        localDate: slot.localDate,
                        firstSlotId: slot.first_slot_id,
                        firstOfGroup: slot.first_slot,
                        lastOfGroup: slot.last_slot,
                        taskId: slot.task_id,
                        showTitle: showSlotTitle,
                        titleWidth: titleWidth,
                        persons: slot.persons,
                        equipments: slot.equipments,
                        teams: slot.teams,
                    };
                });

                let rowHeight = 0;

                if (currentZoom === 0 && maxHourRow !== null) {
                    rowHeight = (maxHourRow + 1) * (state.slotTitlesHeigth + 5) /*margin*/;
                } else {
                    // Sort slots of each day by start time
                    for (let date in itemSlotsForDate) {
                        itemSlotsForDate[date].sort((a, b) => {
                            // first_slot_id is added to the string so that slots where time is the same also keep the same order depending on the first_slot_id
                            return a.time_start + a.first_slot_id > b.time_start + b.first_slot_id
                                ? 1
                                : -1;
                        });
                    }

                    // Calculate the positions
                    let date = firstCalendarDate;
                    const dateEnd = lastCalendarDate;

                    while (!date.isAfter(dateEnd)) {
                        let resetToPreviousDate = false;

                        let dayHeight = 0;
                        let dayStartPosition = 0;

                        const dateString = date.format(dateformatter);
                        let slotsForDate = itemSlotsForDate[dateString];

                        // Skip if there are no slots
                        if (slotsForDate == undefined || slotsForDate.length < 1) {
                            date = date.plusDays(1);
                            continue;
                        }

                        slotsForDate.forEach((slot) => {
                            let slotHeight = Math.ceil((slot.duration / 60) * hourHeight);
                            if (slotHeight < ZOOM_SETTINGS[currentZoom].minSlotHeight) {
                                slotHeight = ZOOM_SETTINGS[currentZoom].minSlotHeight;
                            }
                            // Add margin before the slot
                            dayStartPosition += 5;

                            // Mapping for this slot
                            let slotGroupMapping = {};

                            let mappingHasChange = false;

                            const oldSlotMapping =
                                itemsSlotsGroupMapping[itemId][slot.first_slot_id];
                            if (!oldSlotMapping) {
                                mappingHasChange = true;
                            } else {
                                if (dayStartPosition > oldSlotMapping.startYPos) {
                                    mappingHasChange = true;
                                    if (
                                        !resetToPreviousDate ||
                                        resetToPreviousDate > slots[slot.first_slot_id].date
                                    ) {
                                        resetToPreviousDate = slots[slot.first_slot_id].localDate;
                                    }
                                } else {
                                    dayStartPosition = oldSlotMapping.startYPos;
                                    slotHeight = oldSlotMapping.height;
                                }
                            }

                            if (mappingHasChange) {
                                slotGroupMapping = {
                                    height: slotHeight,
                                    startYPos: dayStartPosition,
                                };

                                itemsSlotsGroupMapping[itemId][
                                    slot.first_slot_id
                                ] = slotGroupMapping;
                            }

                            // Set the starpos for the next slot
                            dayStartPosition += slotHeight;
                        });

                        // Check if this is the heighest date
                        if (rowHeight < dayStartPosition) {
                            rowHeight = dayStartPosition;
                        }

                        // Next day
                        if (resetToPreviousDate) {
                            date = resetToPreviousDate;
                        } else {
                            date = date.plusDays(1);
                        }
                    }
                }

                // Add margin
                rowHeight += 5;
                // Check if row is to small
                if (rowHeight < defaultRowHeight) {
                    rowHeight = defaultRowHeight;
                }

                const itemRowHeight = rowHeight;
                if (row.showGroupRow) {
                    rowHeight += 25;
                }

                rowsCalculation[row.index] = {
                    height: rowHeight,
                    itemRowHeight: itemRowHeight,
                };
            });

            // Only continue if mode is still capacity
            // This may occur when switching and calcultations not done yet.
            // Not the best solution?
            if (state.mode.indexOf('capacity') !== -1) {
                return;
            }

            commit('setRowsCalculation', {
                rowsCalculation: rowsCalculation,
                itemsSlotsGroupMapping: itemsSlotsGroupMapping,
                itemsSlotsMapping: itemsSlotsMapping,
            });
        },
        calculateAbsencePositions({ state, commit }, { itemsIds }) {
            itemsIds = itemsIds.map(Number);

            const rows = state.rows;
            const itemsOrder = state.itemsOrder;
            const companyAbsences = state.companyAbsences;
            const itemsAbsences = state.itemsAbsences;

            const calendarDates = state.dates;
            const firstCalendarDate = calendarDates[0].localDate;
            const lastCalendarDate = calendarDates[calendarDates.length - 1].localDate;

            const currentZoom = state.currentZoom;
            const hourHeight = ZOOM_SETTINGS[currentZoom].hourHeight;
            const dayWidths = ZOOM_SETTINGS[currentZoom].dayWidth;

            const mode = state.mode;

            let absenceMapping = {};

            rows.forEach((row) => {
                const itemId = itemsOrder[row.index];

                if (!itemId || itemsIds.indexOf(itemId) < 0) {
                    return;
                }

                const item = items[itemId];
                if (!item) {
                    return;
                }
                const itemAbsences = itemsAbsences[itemId];

                let itemAbsencesForDate = {};

                absenceMapping[itemId] = {};

                calendarDates.forEach((date) => {
                    absenceMapping[itemId][date.formatted] = {};
                });

                for (const [date, absence] of Object.entries(itemsAbsences)) {
                    // if (
                    //     slotDate.isBefore(firstCalendarDate) ||
                    //     slotDate.isAfter(lastCalendarDate)
                    // ) {
                    //     return;
                    // }

                    // Index by date
                    if (!itemAbsencesForDate[date]) {
                        itemAbsencesForDate[date] = [];
                    }
                    itemAbsencesForDate[date].push(absence);

                    absenceMapping[itemId][date] = absence;
                }

                // Sort slots of each day by start time
                for (let date in itemAbsencesForDate) {
                    itemAbsencesForDate[date].sort((a, b) => {
                        return a.start > b.start ? 1 : -1;
                    });
                }

                // Calculate the positions
                let date = firstCalendarDate;
                const dateEnd = lastCalendarDate;

                let rowHeight = 0;

                while (!date.isAfter(dateEnd)) {
                    let dayStartPosition = 0;

                    const dateString = date.format(dateformatter);
                    let absencesForDate = itemAbsencesForDate[dateString];

                    // Skip if there are no slots
                    if (absencesForDate == undefined || absencesForDate.length < 1) {
                        date = date.plusDays(1);
                        continue;
                    }

                    absencesForDate.forEach((absence) => {
                        let absenceHeight = Math.ceil((absence.duration_minutes / 60) * hourHeight);
                        if (absenceHeight < ZOOM_SETTINGS[currentZoom].minSlotHeight) {
                            absenceHeight = ZOOM_SETTINGS[currentZoom].minSlotHeight;
                        }

                        // Set the starpos for the next slot
                        dayStartPosition += absenceHeight;
                    });

                    date = date.plusDays(1);
                }

                const itemRowHeight = rowHeight;
                if (row.showGroupRow) {
                    rowHeight += 25;
                }

                rowsCalculation[row.index] = {
                    height: rowHeight,
                    itemRowHeight: itemRowHeight,
                };
            });

            commit('setRowsCalculation', {
                rowsCalculation: rowsCalculation,
                itemsSlotsGroupMapping: itemsSlotsGroupMapping,
                itemsSlotsMapping: itemsSlotsMapping,
            });
        },
        setDraggingData({ state, commit, dispatch }, { date, itemId, dragOverTime }) {
            // Don't continue if there is nog dragstart
            // Might be deleted by a drop already, but draggingdata is set after toggle
            if (!state.dragStartInfo.draggedSlot) {
                return;
            }
            const [slots, persons, equipments, tasks, teams] = calculateDragChanges(
                state,
                date,
                itemId,
                dragOverTime,
            );

            // Set the changes
            commit('setDragChanges', {
                slots: slots,
                persons: persons,
                equipments: equipments,
                tasks: tasks,
                teams: teams,
            });

            // Recalculate the positions for the affected items
            const mode = state.mode;
            let itemsToReload = [];
            if (mode === 'project') {
                itemsToReload = Object.keys(tasks);
            } else if (mode === 'person') {
                itemsToReload = Object.keys(persons);
            }  else if (mode === 'equipment') {
                itemsToReload = Object.keys(equipments);
            } else if (mode === 'team') {
                itemsToReload = Object.keys(teams);
            }
            dispatch('calculateSlotPositions', { itemsIds: itemsToReload });
        },
    },
};
